import type { ReactNode } from 'react';

import { AppIcons, type AppIconName } from '@web/ui/core/atoms';
import { twcn } from '@web/ui/core/utils';

/**
 * Key/value grid vocabulary from the "Viajante" handoff: a two-column grid of
 * stacked label/value pairs separated by hairlines, with an icon + mono eyebrow
 * section head. Distinct from the single-column label-left `Section`/`Row`
 * (which the card-detail screen still uses) — this is the editorial 2-col block.
 *
 * - `KvSection` — titled card (icon + eyebrow) wrapping the grid (Perfil, Empresa).
 * - `KvGrid` — the bordered grid box without a head (the drawer detail block).
 * - `Kv` — one label/value cell. `wide` spans the row (place it last); `mono`
 *   renders the value monospace; `empty` greys it for a labelled-empty value.
 *
 * Collapses to a single column on small viewports. Place any `wide` cell last so
 * the `nth-child` hairline parity of the cells before it stays correct.
 */

function KvCells({
  oneColumn,
  children,
}: Readonly<{ oneColumn?: boolean; children: ReactNode }>): React.JSX.Element {
  return <div className={twcn('grid grid-cols-1', !oneColumn && 'sm:grid-cols-2')}>{children}</div>;
}

export function KvSection({
  icon,
  label,
  oneColumn,
  className,
  children,
}: Readonly<{
  icon: AppIconName;
  label: string;
  oneColumn?: boolean;
  className?: string;
  children: ReactNode;
}>): React.JSX.Element {
  return (
    <section
      className={twcn('overflow-hidden rounded-lg border border-border-soft bg-surface', className)}
    >
      <header className="flex items-center gap-2 border-b border-border-soft px-4 py-2.75">
        <AppIcons name={icon} size={14} className="text-faint-foreground" />
        <span className="font-mono text-tiny font-medium uppercase tracking-eyebrow-wide text-muted-foreground">
          {label}
        </span>
      </header>
      <KvCells oneColumn={oneColumn}>{children}</KvCells>
    </section>
  );
}

export function KvGrid({
  oneColumn,
  children,
}: Readonly<{ oneColumn?: boolean; children: ReactNode }>): React.JSX.Element {
  return (
    <div className="overflow-hidden rounded-lg border border-border-soft">
      <KvCells oneColumn={oneColumn}>{children}</KvCells>
    </div>
  );
}

export function Kv({
  label,
  mono,
  empty,
  wide,
  children,
}: Readonly<{
  label: string;
  mono?: boolean;
  empty?: boolean;
  wide?: boolean;
  children: ReactNode;
}>): React.JSX.Element {
  return (
    <div
      className={twcn(
        'flex min-w-0 flex-col gap-0.75 border-border-soft px-4 py-2.75 border-b last:border-b-0',
        // Drop the bottom hairline on the last row's left cell in 2-col layouts.
        'sm:[&:nth-last-child(2):nth-child(odd)]:border-b-0',
        wide ? 'col-span-full' : 'sm:[&:nth-child(odd)]:border-r',
      )}
    >
      <span className="font-mono text-2xs font-medium uppercase tracking-eyebrow-loose text-faint-foreground">
        {label}
      </span>
      <span
        className={twcn(
          'break-words text-card-meta leading-card-meta text-foreground',
          mono && 'font-mono text-meta tracking-display-tight',
          empty && 'text-faint-foreground',
        )}
      >
        {children}
      </span>
    </div>
  );
}
